import { useState } from "react";
import { useAuth } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Phone, MessageCircle, MapPin, Home, Eye, FileText } from "lucide-react";

// House locations with their full addresses
const HOUSE_LOCATIONS = [
  { id: "dada_estate", name: "Halelluyah Area, Dada Estate, Osogbo" },
  { id: "ile_tuntun", name: "Aina Close, Ile-TunTun, Ibadan" },
  { id: "adetunji", name: "Adetunji Area, Osogbo" },
  { id: "aimasiko", name: "Aimasiko Area, Osogbo" },
  { id: "alakia", name: "Alakia Area, Ibadan" },
];

// Apartments/Units for each location
const APARTMENTS_BY_LOCATION = {
  "dada_estate": [
    "One-Bedroom unit 1",
    "One-Bedroom unit 2",
    "One-Bedroom unit 3",
    "Two-Bedroom unit"
  ],
  "ile_tuntun": [
    "One-Bedroom unit 1",
    "One-Bedroom unit 2",
    "Three-Bedroom unit"
  ],
  "adetunji": [
    "Unit 1",
    "Unit 2",
    "Unit 3",
    "Unit 4"
  ],
  "aimasiko": [
    "Unit 1",
    "Unit 2",
    "Unit 3",
    "Unit 4",
    "Unit 5"
  ],
  "alakia": [
    "Unit 1",
    "Unit 2",
    "Unit 3",
    "Unit 4",
    "Unit 5",
    "Unit 6",
    "Unit 7",
    "Unit 8",
    "Unit 9",
    "Unit 10"
  ]
};

const PAYMENT_FREQUENCIES = [
  "Monthly",
  "Quarterly",
  "Every six months",
  "Yearly"
];

export default function AdminFormView() {
  const { user } = useAuth();
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    mobile_phone: "",
    whatsapp_phone: "",
    house_location: "",
    apartment_number: "",
    payment_frequency: "",
    duration_living: "",
    last_payment_amount: "",
    last_payment_date: "",
    months_years_in_arrears: "",
    amount_in_arrears: "",
    next_payment_date: ""
  });

  // Get available apartments based on selected location
  const availableApartments = selectedLocationId ? APARTMENTS_BY_LOCATION[selectedLocationId] || [] : [];

  // Handle location change
  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId);
    const location = HOUSE_LOCATIONS.find(loc => loc.id === locationId);
    setFormData(prev => ({
      ...prev,
      house_location: location ? location.name : "",
      apartment_number: ""
    }));
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const resetForm = () => {
    setSelectedLocationId("");
    setFormData({
      last_name: "",
      first_name: "",
      mobile_phone: "",
      whatsapp_phone: "",
      house_location: "",
      apartment_number: "",
      payment_frequency: "",
      duration_living: "",
      last_payment_amount: "",
      last_payment_date: "",
      months_years_in_arrears: "",
      amount_in_arrears: "",
      next_payment_date: ""
    });
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-form-view-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-primary">Form View</h1>
                <p className="text-text-muted">Preview the Tenant Information Update form</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Badge className="bg-blue-100 text-blue-800">
                <Eye className="w-3 h-3 mr-1" />
                Preview Mode
              </Badge>
              <Badge className="bg-stone-100 text-stone-600">
                Admin Only
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetForm}
                className="ml-auto"
              >
                Reset Form
              </Button>
            </div>
          </div>

          {/* Info Banner */}
          <Card className="border-blue-200 bg-blue-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800">
                    This is a preview of the <strong>Tenant Information Update</strong> form that tenants see when they log in. 
                    You can interact with the form to test the dropdowns and see how it works, but submissions are disabled in preview mode.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Preview */}
          <Card className="border-stone-200">
            <CardHeader className="border-b border-stone-100">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Tenant Information Update Form
              </CardTitle>
              <CardDescription>
                This is exactly what tenants see when updating their information
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Personal Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary">Personal Details</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        placeholder="Enter your last name"
                        value={formData.last_name}
                        onChange={(e) => handleChange("last_name", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        placeholder="Enter your first name"
                        value={formData.first_name}
                        onChange={(e) => handleChange("first_name", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Contact Information */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Update Contact Information (at least one required)
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mobile_phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-stone-500" />
                        Mobile Tel #
                      </Label>
                      <Input
                        id="mobile_phone"
                        placeholder="e.g., +234 801 234 5678"
                        value={formData.mobile_phone}
                        onChange={(e) => handleChange("mobile_phone", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp_phone" className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-green-600" />
                        WhatsApp # (preferred)
                      </Label>
                      <Input
                        id="whatsapp_phone"
                        placeholder="e.g., +234 801 234 5678"
                        value={formData.whatsapp_phone}
                        onChange={(e) => handleChange("whatsapp_phone", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    WhatsApp is preferred for faster communication
                  </p>
                </fieldset>

                {/* Location Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location Details
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-stone-500" />
                        House Location *
                      </Label>
                      <Select 
                        value={selectedLocationId} 
                        onValueChange={handleLocationChange}
                      >
                        <SelectTrigger className="h-12 bg-stone-50 border-stone-200">
                          <SelectValue placeholder="Select your house location" />
                        </SelectTrigger>
                        <SelectContent>
                          {HOUSE_LOCATIONS.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-stone-500" />
                        Apartment/Unit Occupied *
                      </Label>
                      <Select 
                        value={formData.apartment_number} 
                        onValueChange={(value) => handleChange("apartment_number", value)}
                        disabled={!selectedLocationId}
                      >
                        <SelectTrigger className="h-12 bg-stone-50 border-stone-200">
                          <SelectValue placeholder={selectedLocationId ? "Select your unit" : "Select location first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableApartments.map((apt) => (
                            <SelectItem key={apt} value={apt}>{apt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!selectedLocationId && (
                        <p className="text-xs text-amber-600">
                          Please select a house location first
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="duration_living">How long have you been living in this apartment/Shop? *</Label>
                      <Input
                        id="duration_living"
                        placeholder="e.g., 2 years 3 months"
                        value={formData.duration_living}
                        onChange={(e) => handleChange("duration_living", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Payment Details */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary">Payment Details</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Frequency *</Label>
                      <Select 
                        value={formData.payment_frequency} 
                        onValueChange={(value) => handleChange("payment_frequency", value)}
                      >
                        <SelectTrigger className="h-12 bg-stone-50 border-stone-200">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_FREQUENCIES.map((freq) => (
                            <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_payment_amount">Last Payment Amount (₦) *</Label>
                      <Input
                        id="last_payment_amount"
                        placeholder="e.g., 250000"
                        value={formData.last_payment_amount}
                        onChange={(e) => handleChange("last_payment_amount", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_payment_date">Date of Last Payment *</Label>
                      <Input
                        id="last_payment_date"
                        type="date"
                        value={formData.last_payment_date}
                        onChange={(e) => handleChange("last_payment_date", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="next_payment_date">Date of Next Payment *</Label>
                      <Input
                        id="next_payment_date"
                        type="date"
                        value={formData.next_payment_date}
                        onChange={(e) => handleChange("next_payment_date", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Arrears Information */}
                <fieldset className="border border-stone-200 rounded-xl p-4">
                  <legend className="px-2 text-sm font-medium text-primary">Arrears Information (if applicable)</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="months_years_in_arrears">Month/Years in Arrears</Label>
                      <Input
                        id="months_years_in_arrears"
                        placeholder="e.g., 3 months"
                        value={formData.months_years_in_arrears}
                        onChange={(e) => handleChange("months_years_in_arrears", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="amount_in_arrears">Amount in Arrears (₦)</Label>
                      <Input
                        id="amount_in_arrears"
                        placeholder="e.g., 150000"
                        value={formData.amount_in_arrears}
                        onChange={(e) => handleChange("amount_in_arrears", e.target.value)}
                        className="h-12 bg-stone-50 border-stone-200"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Submit Button (Disabled in preview) */}
                <div className="flex justify-end pt-4">
                  <Button 
                    disabled
                    className="bg-stone-400 cursor-not-allowed rounded-full px-8"
                  >
                    Submit (Disabled in Preview)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
